import { LIMITS } from './constants';
import type { OpenStatus, OpeningHours, SpecialHours } from './types';

export interface HoursContext {
  opening: Pick<OpeningHours, 'weekday' | 'opens' | 'closes'>[];
  special: Pick<SpecialHours, 'date' | 'closed' | 'opens' | 'closes'>[];
  timezone: string;
}

export interface LocalParts {
  /** 'YYYY-MM-DD' in the location timezone. */
  date: string;
  /** 0 = Sunday … 6 = Saturday */
  weekday: number;
  /** minutes since local midnight */
  minutes: number;
}

export interface ReservationSlot {
  /** ISO UTC instant. */
  start: string;
  end: string;
  /** Local display label, e.g. "7:30 PM". */
  label: string;
}

const DAY_MS = 86_400_000;

// ---------------------------------------------------------------------------
// Timezone helpers (Intl-based; work identically in workerd and browsers)
// ---------------------------------------------------------------------------

const partsFormatterCache = new Map<string, Intl.DateTimeFormat>();

function partsFormatter(timezone: string): Intl.DateTimeFormat {
  let formatter = partsFormatterCache.get(timezone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    partsFormatterCache.set(timezone, formatter);
  }
  return formatter;
}

function partValue(parts: Intl.DateTimeFormatPart[], type: string): number {
  const value = parts.find((part) => part.type === type)?.value;
  return value ? Number.parseInt(value, 10) : 0;
}

/** Offset of `timezone` at a given UTC instant, in milliseconds. */
export function timezoneOffsetMs(timezone: string, utcMs: number): number {
  const parts = partsFormatter(timezone).formatToParts(new Date(utcMs));
  const asUtc = Date.UTC(
    partValue(parts, 'year'),
    partValue(parts, 'month') - 1,
    partValue(parts, 'day'),
    partValue(parts, 'hour'),
    partValue(parts, 'minute'),
    partValue(parts, 'second'),
  );
  return asUtc - utcMs;
}

export function localParts(instant: Date | number, timezone: string): LocalParts {
  const date = typeof instant === 'number' ? new Date(instant) : instant;
  const parts = partsFormatter(timezone).formatToParts(date);
  const year = partValue(parts, 'year');
  const month = String(partValue(parts, 'month')).padStart(2, '0');
  const day = String(partValue(parts, 'day')).padStart(2, '0');
  const dateStr = `${year}-${month}-${day}`;
  const weekday = new Date(Date.UTC(year, Number(month) - 1, Number(day))).getUTCDay();
  return {
    date: dateStr,
    weekday,
    minutes: partValue(parts, 'hour') * 60 + partValue(parts, 'minute'),
  };
}

/**
 * Converts a wall-clock time in a timezone to the UTC instant.
 * Handles DST transitions: ambiguous times pick the first occurrence, skipped
 * times shift forward by the gap.
 */
export function zonedToUtc(dateStr: string, timeStr: string, timezone: string): Date {
  const [year, month, day] = dateStr.split('-').map((value) => Number.parseInt(value, 10));
  const [hour, minute, second = 0] = timeStr.split(':').map((value) => Number.parseInt(value, 10));
  if (!year || !month || !day || Number.isNaN(hour) || Number.isNaN(minute)) {
    throw new Error(`Invalid date/time: ${dateStr} ${timeStr}`);
  }
  const guess = Date.UTC(year, month - 1, day, hour, minute, second);
  const firstOffset = timezoneOffsetMs(timezone, guess);
  let utc = guess - firstOffset;
  const secondOffset = timezoneOffsetMs(timezone, utc);
  if (secondOffset !== firstOffset) utc = guess - secondOffset;

  // Nonexistent wall times (a spring-forward gap) must shift FORWARD by the gap.
  // The offset correction above can otherwise land before the gap again.
  const local = localParts(utc, timezone);
  const requestedMinutes = (hour ?? 0) * 60 + (minute ?? 0);
  if (local.date === dateStr && local.minutes !== requestedMinutes) {
    utc += (requestedMinutes - local.minutes) * 60_000;
  }

  return new Date(utc);
}

export function addDays(dateStr: string, days: number): string {
  const [year, month, day] = dateStr.split('-').map((value) => Number.parseInt(value, 10));
  const next = new Date(Date.UTC(year!, month! - 1, day! + days));
  return next.toISOString().slice(0, 10);
}

export function weekdayOf(dateStr: string): number {
  const [year, month, day] = dateStr.split('-').map((value) => Number.parseInt(value, 10));
  return new Date(Date.UTC(year!, month! - 1, day!)).getUTCDay();
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

const timeFormatterCache = new Map<string, Intl.DateTimeFormat>();

function timeFormatter(timezone: string, withDate: boolean): Intl.DateTimeFormat {
  const key = `${timezone}|${withDate}`;
  let formatter = timeFormatterCache.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      ...(withDate
        ? { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' as const }
        : {}),
    });
    timeFormatterCache.set(key, formatter);
  }
  return formatter;
}

/** "7:30 PM" */
export function formatLocalTime(instant: Date | string, timezone: string): string {
  const date = typeof instant === 'string' ? new Date(instant) : instant;
  return timeFormatter(timezone, false).format(date);
}

/** "Sat, Sep 26, 2026, 7:30 PM" */
export function formatLocalDateTime(instant: Date | string, timezone: string): string {
  const date = typeof instant === 'string' ? new Date(instant) : instant;
  return timeFormatter(timezone, true).format(date);
}

/** minutes since midnight → "2:00 AM" */
export function minutesToLabel(minutes: number): string {
  const normalized = ((minutes % 1440) + 1440) % 1440;
  const hour = Math.floor(normalized / 60);
  const minute = normalized % 60;
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(new Date(Date.UTC(2000, 0, 1, hour, minute)));
}

export function parseTimeToMinutes(value: string): number | null {
  const match = value.match(/^(\d{2}):(\d{2})(?::\d{2})?$/);
  if (!match) return null;
  const hours = Number.parseInt(match[1]!, 10);
  const minutes = Number.parseInt(match[2]!, 10);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

// ---------------------------------------------------------------------------
// Open/closed logic
// ---------------------------------------------------------------------------

interface LocalRange {
  start: number;
  /** May exceed 1440 for overnight ranges. */
  end: number;
}

/** Effective ranges for a local calendar date, with special hours overriding. */
export function rangesForDate(ctx: HoursContext, dateStr: string): LocalRange[] {
  const special = ctx.special.find((entry) => entry.date === dateStr);
  if (special) {
    if (special.closed || !special.opens || !special.closes) return [];
    const start = parseTimeToMinutes(special.opens);
    const end = parseTimeToMinutes(special.closes);
    if (start == null || end == null) return [];
    return [{ start, end: end <= start ? end + 1440 : end }];
  }
  const weekday = weekdayOf(dateStr);
  return ctx.opening
    .filter((entry) => Number(entry.weekday) === weekday)
    .map((entry) => {
      const start = parseTimeToMinutes(entry.opens);
      const end = parseTimeToMinutes(entry.closes);
      if (start == null || end == null) return null;
      return { start, end: end <= start ? end + 1440 : end };
    })
    .filter((range): range is LocalRange => range !== null)
    .sort((a, b) => a.start - b.start);
}

interface ActiveRange {
  range: LocalRange;
  /** minutes of the current local day, possibly +1440 when the range began yesterday */
  nowMinutes: number;
}

function findActiveRange(ctx: HoursContext, dateStr: string, minutes: number): ActiveRange | null {
  for (const range of rangesForDate(ctx, dateStr)) {
    if (minutes >= range.start && minutes < range.end) return { range, nowMinutes: minutes };
  }
  const yesterday = addDays(dateStr, -1);
  for (const range of rangesForDate(ctx, yesterday)) {
    if (range.end <= 1440) continue;
    const shifted = minutes + 1440;
    if (shifted >= range.start && shifted < range.end) return { range, nowMinutes: shifted };
  }
  return null;
}

export function getOpenStatus(ctx: HoursContext, now: Date = new Date()): OpenStatus {
  const local = localParts(now, ctx.timezone);
  const active = findActiveRange(ctx, local.date, local.minutes);

  if (active) {
    const closeMinutes = active.range.end - active.nowMinutes + local.minutes;
    return {
      open: true,
      next_open_at: null,
      next_open_label: null,
      closes_at_label: minutesToLabel(closeMinutes),
    };
  }

  // Find the next opening: scan today (later ranges) then upcoming days.
  for (let dayOffset = 0; dayOffset <= 14; dayOffset += 1) {
    const dateStr = addDays(local.date, dayOffset);
    for (const range of rangesForDate(ctx, dateStr)) {
      const startMinutes = range.start;
      if (dayOffset === 0 && startMinutes <= local.minutes) continue;
      const instant = zonedToUtc(
        startMinutes >= 1440 ? addDays(dateStr, 1) : dateStr,
        minutesToTime(startMinutes % 1440),
        ctx.timezone,
      );
      return {
        open: false,
        next_open_at: instant.toISOString(),
        next_open_label: formatLocalTime(instant, ctx.timezone),
        closes_at_label: null,
      };
    }
  }

  return { open: false, next_open_at: null, next_open_label: null, closes_at_label: null };
}

function minutesToTime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:00`;
}

// ---------------------------------------------------------------------------
// Reservation slots
// ---------------------------------------------------------------------------

export interface ReservationSlotOptions {
  ctx: HoursContext;
  settings: {
    slot_minutes: number;
    min_notice_minutes: number;
    max_days_ahead: number;
  };
  /** 'YYYY-MM-DD' in the location timezone. */
  date: string;
  now?: Date;
  /** Safety bound on returned slots. */
  maxSlots?: number;
}

export function generateReservationSlots(options: ReservationSlotOptions): ReservationSlot[] {
  const { ctx, settings, date } = options;
  const now = options.now ?? new Date();
  const today = localParts(now, ctx.timezone);

  if (date < today.date) return [];
  const maxDate = addDays(today.date, settings.max_days_ahead);
  if (date > maxDate) return [];

  const slotMinutes = Math.max(5, settings.slot_minutes || 30);
  const earliest = now.getTime() + Math.max(0, settings.min_notice_minutes) * 60_000;
  const slots: ReservationSlot[] = [];
  const maxSlots = options.maxSlots ?? 60;

  for (const range of rangesForDate(ctx, date)) {
    let start = Math.ceil(range.start / slotMinutes) * slotMinutes;
    if (start < range.start) start = range.start;
    for (; start + slotMinutes <= range.end; start += slotMinutes) {
      const localDate = start >= 1440 ? addDays(date, 1) : date;
      const instant = zonedToUtc(localDate, minutesToTime(start % 1440), ctx.timezone);
      // The minimum-notice window applies to every candidate instant, not just
      // to slots later today (tomorrow's early slots can still be too soon).
      if (instant.getTime() < earliest) continue;
      const end = new Date(instant.getTime() + slotMinutes * 60_000);
      slots.push({ start: instant.toISOString(), end: end.toISOString(), label: formatLocalTime(instant, ctx.timezone) });
    }
  }

  // DST transitions can map two wall-clock candidates to the same instant.
  const seen = new Set<string>();
  const unique = slots.filter((slot) => {
    if (seen.has(slot.start)) return false;
    seen.add(slot.start);
    return true;
  });

  return unique.sort((a, b) => a.start.localeCompare(b.start)).slice(0, maxSlots);
}

/** Whole days between two 'YYYY-MM-DD' strings. */
export function daysBetween(from: string, to: string): number {
  const [fy, fm, fd] = from.split('-').map(Number);
  const [ty, tm, td] = to.split('-').map(Number);
  return Math.round((Date.UTC(ty!, tm! - 1, td!) - Date.UTC(fy!, fm! - 1, fd!)) / DAY_MS);
}

/** Formats a wall-clock "HH:MM:SS" as "5:00 PM" without needing a date. */
export function timeStringToLabel(time: string): string {
  const minutes = parseTimeToMinutes(time);
  return minutes == null ? time : minutesToLabel(minutes);
}

export const DEFAULT_RESERVATION_SETTINGS = {
  min_party: 1,
  max_party: 10,
  slot_minutes: 30,
  max_days_ahead: 30,
  min_notice_minutes: 60,
  auto_print: true,
} as const;

export const MAX_RESERVATION_PARTY = Math.min(100, LIMITS.maxLineQuantity);
